/// A post as it appears in the list. Three rendering modes:
///   - developer post: emerald left border + faint emerald tint
///   - feedback post (from the in-game Feedback flow): amber star
///     chip + level pin where the byline would be; no name
///     attached. The submitter UID lives privately in
///     `webFeedbackOwners/{postId}` and never reaches this card.
///   - regular Q&A post: byline + title + body excerpt

import { isFeedbackPost, type DevPost, type VoteValue } from '../data/devBlog'
import { DevBlogByline } from './DevBlogByline'
import { DevBlogFeedbackHeader } from './DevBlogFeedbackHeader'
import { DevBlogVoteControl } from './DevBlogVoteControl'

export function DevBlogPostCard(props: {
  post: DevPost
  myVote: VoteValue | null
  voteDisabled?: boolean
  onVote: (next: VoteValue | null) => void
  onOpen: () => void
}) {
  const dev = props.post.authorIsDeveloper
  const feedback = isFeedbackPost(props.post)
  const replies = props.post.commentCount
  return (
    <article
      className={
        'flex gap-3 rounded-lg p-3 ring-1 ' +
        (dev
          ? 'border-l-4 border-emerald-500 bg-emerald-500/5 ring-emerald-400/20'
          : feedback
            ? 'border-l-4 border-amber-400/70 bg-amber-400/[0.04] ring-amber-300/15'
            : 'bg-slate-900/60 ring-white/10')
      }
    >
      <DevBlogVoteControl
        score={props.post.score}
        myVote={props.myVote}
        disabled={props.voteDisabled}
        onVote={props.onVote}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {feedback ? (
            <DevBlogFeedbackHeader
              rating={props.post.feedbackRating ?? 0}
              levelId={props.post.feedbackLevelId}
              createdAt={props.post.createdAt}
            />
          ) : (
            <DevBlogByline
              authorName={props.post.authorName}
              authorUid={props.post.authorUid}
              authorIsDeveloper={dev}
              createdAt={props.post.createdAt}
              editedAt={props.post.editedAt}
            />
          )}
          {props.post.archivedAt && (
            <span className="rounded bg-slate-700/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300 ring-1 ring-slate-500/40">
              Archived
            </span>
          )}
        </div>
        <button type="button" onClick={props.onOpen} className="mt-1 block w-full text-left">
          <h3 className="truncate text-sm font-semibold text-white hover:underline">{props.post.title}</h3>
        </button>
        <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap break-words text-xs text-slate-400">{props.post.body}</p>
        <button
          type="button"
          onClick={props.onOpen}
          className="mt-1.5 text-[11px] font-medium text-emerald-300 hover:underline"
        >
          {replies === 1 ? '1 reply' : `${replies} replies`} →
        </button>
      </div>
    </article>
  )
}
